<template>
    <div class="page-body add-notification-app-modal">
        <NoButtonModal :open="open" @close="$emit('close')">
            <div class="modal-content-header bottom-20">
                <span style="font-size: 20px; font-weight: bold; margin-left: 20px">Add Notification Templates</span>
            </div>

            <div
                v-if="loadingBuilderApplications"
                class="my-4 d-flex flex-column justify-content-center align-items-center loader-main"
            >
                <b-spinner variant="primary" class="mb-1"></b-spinner>
                <span class="ms-3">Loading ...</span>
            </div>

            <form v-else class="add-new-app-form" @submit.prevent="submit">
                <div class="d-flex flex-column align-items-center" style="gap: 10px">
                    <p class="w-80 mx-4 modal-subtitle">
                        Select an existing Application Builder application to configure notification templates for.
                        Templates can be filled in later.
                    </p>

                    <div class="w-80 mx-4 d-flex justify-content-center flex-column">
                        <label for="add-app-builder" class="add-new-app-label my-2 class-label">
                            Select Application*
                        </label>
                        <Multiselect
                            id="add-app-builder"
                            track-by="id"
                            label="application_name"
                            class="position-relative"
                            placeholder="Search or select Application..."
                            v-model="selectedBuilderApplication"
                            :options="availableBuilderApplications"
                            :searchable="true"
                            :allow-empty="false"
                            :show-labels="false"
                            :disabled="builderApplicationsLoadFailed"
                        />
                        <span v-if="builderApplicationsLoadFailed" class="field-help field-help-error">
                            Application Builder applications could not be loaded. Close this dialog and try again.
                        </span>
                        <span v-else-if="!availableBuilderApplications.length" class="field-help">
                            All available Application Builder applications already have notification templates.
                        </span>
                    </div>

                    <div class="w-80 mx-4 d-flex flex-column">
                        <Input
                            type="text"
                            label="iOS App Store URL"
                            name="iOS App Store URL"
                            id="add-app-ios"
                            placeholder="https://apps.apple.com/…"
                            v-model="form.url_ios"
                            labelClass="add-new-app-label class-label my-2"
                        />
                        <Input
                            type="text"
                            label="Android Play Store URL"
                            name="Android Play Store URL"
                            id="add-app-android"
                            placeholder="https://play.google.com/…"
                            v-model="form.url_android"
                            labelClass="add-new-app-label class-label my-2"
                        />
                        <Input
                            type="text"
                            label="Shortened iOS URL"
                            name="Shortened iOS URL"
                            id="add-app-ios-short"
                            placeholder="https://mh2.co/…"
                            v-model="form.url_ios_shortened"
                            labelClass="add-new-app-label class-label my-2"
                        />
                        <Input
                            type="text"
                            label="Shortened Android URL"
                            name="Shortened Android URL"
                            id="add-app-android-short"
                            placeholder="https://mh2.co/…"
                            v-model="form.url_android_shortened"
                            labelClass="add-new-app-label class-label my-2"
                        />
                    </div>

                    <div class="w-80 mx-4 d-flex justify-content-center flex-column">
                        <label for="add-app-copy" class="add-new-app-label my-2 class-label">Copy Templates From</label>
                        <Multiselect
                            id="add-app-copy"
                            track-by="id"
                            label="name"
                            class="position-relative"
                            placeholder="Search or select Application..."
                            v-model="copyFromApplication"
                            :options="copyFromOptions"
                            :searchable="true"
                            :allow-empty="false"
                            :show-labels="false"
                        />
                    </div>

                    <div class="my-4 d-flex justify-content-center">
                        <button type="submit" class="primary btn-save" :disabled="!canSubmit">
                            {{ submitting ? 'Saving ...' : 'Save' }}
                        </button>
                        <button type="button" class="no-bg btn-cancel px-4" :disabled="submitting" @click="$emit('close')">
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </NoButtonModal>
    </div>
</template>

<script>
    import NoButtonModal from '@/components/general/modals/NoButtonModal.vue';
    import { applicationBuilder, applications as applicationsApi } from '@/util/apiRequests';
    import { TEMPLATE_FIELDS } from './notificationTemplates.constants';

    const START_BLANK_OPTION = { id: null, name: 'Start blank — configure templates later' };

    function emptyForm() {
        return {
            url_ios: '',
            url_android: '',
            url_ios_shortened: '',
            url_android_shortened: '',
        };
    }

    export default {
        name: 'AddApplicationModal',
        components: {
            NoButtonModal,
        },
        props: {
            open: {
                type: Boolean,
                default: false,
            },
            applications: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                form: emptyForm(),
                selectedBuilderApplication: null,
                copyFromApplication: START_BLANK_OPTION,
                submitting: false,
                loadingBuilderApplications: false,
                builderApplicationsLoadFailed: false,
                builderApplications: [],
            };
        },
        computed: {
            availableBuilderApplications() {
                const configuredNames = new Set(
                    this.applications.map((application) => (application.name || '').trim().toLowerCase())
                );

                return this.builderApplications.filter((application) => {
                    const name = (application.application_name || '').trim().toLowerCase();
                    return name && !configuredNames.has(name);
                });
            },
            copyFromOptions() {
                return [START_BLANK_OPTION, ...this.applications];
            },
            canSubmit() {
                return Boolean(this.selectedBuilderApplication) && !this.loadingBuilderApplications && !this.submitting;
            },
        },
        watch: {
            open(isOpen) {
                if (isOpen) {
                    this.form = emptyForm();
                    this.selectedBuilderApplication = null;
                    this.copyFromApplication = START_BLANK_OPTION;
                    this.submitting = false;
                    this.loadBuilderApplications();
                }
            },
        },
        methods: {
            async loadBuilderApplications() {
                this.loadingBuilderApplications = true;
                this.builderApplicationsLoadFailed = false;
                this.builderApplications = [];
                try {
                    const res = await this.$api.get(applicationBuilder.getAllApplications());
                    this.builderApplications = res.data?.data || [];
                } catch (err) {
                    this.builderApplicationsLoadFailed = true;
                    this.$toasted.error('Failed to load Application Builder applications. Please try again later.');
                } finally {
                    this.loadingBuilderApplications = false;
                }
            },
            buildPayload() {
                const payload = { application_builder_id: this.selectedBuilderApplication.id };

                for (const field of ['url_ios', 'url_android', 'url_ios_shortened', 'url_android_shortened']) {
                    if (this.form[field].trim()) {
                        payload[field] = this.form[field].trim();
                    }
                }

                if (this.copyFromApplication && this.copyFromApplication.id) {
                    const source = this.applications.find(
                        (application) => application.id === this.copyFromApplication.id
                    );
                    if (source) {
                        for (const field of TEMPLATE_FIELDS) {
                            if (source[field]) {
                                payload[field] = source[field];
                            }
                        }
                    }
                }

                return payload;
            },
            async submit() {
                if (!this.canSubmit) {
                    return;
                }

                this.submitting = true;
                try {
                    const res = await this.$api.post(applicationsApi.createNotificationTemplates(), this.buildPayload());
                    this.$emit('created', res.data?.data);
                } catch (err) {
                    const message = err?.response?.data?.message;
                    this.$toasted.error(message || 'Failed to add notification templates. Please try again later.');
                } finally {
                    this.submitting = false;
                }
            },
        },
    };
</script>

<style scoped>
    .modal-content-header {
        background-color: #f1f7ff;
        padding: 10px;
        display: flex;
        align-items: center;
    }

    .modal-subtitle {
        font-size: 0.85rem;
        color: #666c6d;
        margin: 0;
    }

    .field-help {
        font-size: 0.8rem;
        color: #666c6d;
        margin-top: 6px;
    }

    .field-help-error {
        color: #ac2734;
    }

    .loader-main {
        min-height: 200px;
    }

    .btn-save {
        border-radius: 10px;
        background: #5155c3 !important;
        box-shadow: 0px 10px 5px 0px rgba(0, 0, 0, 0.03);
    }

    .btn-save:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    button.no-bg:hover {
        background: none;
    }

    .btn-cancel,
    .btn-cancel:hover {
        border-radius: 10px;
        border: 2px solid #ac2734;
    }
</style>

<style>
    .page-body.add-notification-app-modal {
        padding: 0;
    }

    .add-notification-app-modal .modal__content {
        padding: 0;
        border-radius: 10px;
        min-height: fit-content;
    }

    /* .page-body form (styles.css) styles forms as shadowed white cards — undo it inside the modal */
    .add-notification-app-modal .modal__content form {
        box-shadow: none;
        padding: 0;
        margin: 0;
        background: transparent;
    }

    /* Match the Multiselect control size (full width, 43px rendered height) so inputs and dropdowns line up */
    .add-notification-app-modal .add-new-app-form input[type='text'] {
        width: 100%;
        min-height: 43px;
        box-sizing: border-box;
        font-size: 14px;
    }

    .add-notification-app-modal .class-label {
        color: #666c6d;
        font-size: 1rem;
        font-weight: 600;
    }
</style>
